/**
 * `ListColumn` — `FE-13.2` (`FR-VIEW-007`), half of `FE-13.3`
 * (`FR-VIEW-010` AC2), and now `FE-12.1`'s two `Add a card` affordances plus
 * the `SortableContext` a card drag needs. `docs/design/c1/C1-BRIEF.md`
 * §7.2/§7.3 fixed the original props and DOM contract; `docs/design/c3/
 * C3-BRIEF.md` §6.5 (as corrected by §12.1, §12.2, §12.4 and §12.13 item 4)
 * fixes what chunk `C3` adds. The caller (`BoardFace`, agent G) wraps this in
 * the `<li>` of `<ol id="board-lists">`.
 *
 * ⚠ **This file renders no `<li>` FOR A CARD.** A card's `<li>` is
 * `SortableCardTile`'s own root, and it is the element that carries the
 * card's drop targets — so anything keyed to "the card's `<li>`" belongs
 * there, not here. The three `<li>` elements this file *does* render wrap
 * the top `CardComposer`, the bottom `CardComposer` and `ListEmpty`; none of
 * them is a card. The older wording — *"this file renders no `<li>` of its
 * own"* — was true of a card and false of the markup, and fan-out 12's brief
 * read it the other way round, citing those three composer/empty line
 * numbers as the home of the card-front accept-ring. They are not.
 * `docs/design/fe12-card-front-sticker-ring/AUDIT.md` §3.1 measures it.
 *
 * ⚠ Renders `list.openCardCount`, never `list.cardCount` — `cardCount`
 * counts archived and open cards alike (`FR-BOARD-016`'s delete
 * confirmation reads it); the two differ on any list holding an archived
 * card. See `boardFaceListSchema.openCardCount`'s docstring in
 * `packages/shared/src/board-contents.ts`.
 *
 * ⚠ `list.colour` is B58's decoration layer. It tints the `ListPanel` wrapper
 * only; card titles, badges and controls keep their own contrast rules rather
 * than inheriting a list-specific text colour.
 *
 * ⚠ `FE-11.x` (chunk `C2`): the header row — drag handle, title, open count,
 * actions menu — is `ListHeader.tsx`, not this file. This file renders it,
 * and now wraps it in a small flex row alongside the new header add-card
 * control (see below) — `ListHeader.tsx` is not this chunk's to edit, so the
 * new control is a SIBLING, not an addition to that file's own markup.
 *
 * ── The scroll contract (`FR-VIEW-010` AC2) ─────────────────────────────
 * Only the card stack (`<ol aria-label="Cards in …">`) scrolls. The header
 * row and the bottom "Add a card" affordance sit outside that scroll
 * container in the same flex column, so they stay reachable while the stack
 * scrolls. This file never puts `overflow-y-auto` on the `<section>` itself
 * — only on the stack.
 *
 * That said, the scroll only activates if an ANCESTOR hands this `<section>`
 * a bounded height. `BoardFace`'s own docstring (agent G) states the exact
 * contract this file has to meet: its `<li>` is `max-h-full min-h-0
 * w-list shrink-0`, so this `<section>` is `flex min-h-0 w-full flex-col
 * max-h-full` (no `h-full` — `<li>`'s own height is intrinsic, capped by
 * `max-h-full`, not stretched) and the card `<ol>` is `flex-1 min-h-0
 * overflow-y-auto`. Width comes from `w-full` filling the `<li>`'s own
 * `w-list`, rather than a second `w-list` here, so the 292px figure has
 * exactly one class-name spelling in the composed tree.
 *
 * ── `C3-BRIEF.md` §6.5 — WHAT CHANGED, chunk `C3` agent F ──────────────────
 *
 * 1. **The card `<ol>` ALWAYS renders now, even when the list is empty**
 *    (§12.13 item 4). It is the drop target that makes dragging a card into
 *    an empty list possible, and `FR-BOARD-025`'s empty-list browser spec
 *    now locates `Cards in <list>` on an empty list as its premise.
 *    `ListEmpty` renders INSIDE it, wrapped in its own `<li>` for valid
 *    `<ol>` markup, rather than replacing it. ⚠ **This disagrees with this
 *    file's own pre-C3 test suite** (`ListColumn.test.tsx`'s "renders
 *    ListEmpty, and no card stack" asserted the opposite), which this
 *    agent's own edit to that file corrects — see the return.
 * 2. **Two add-card controls per list now** (§7.1): the bottom one keeps
 *    C1's exact accessible name (`Add a card to <title>`) and callback
 *    target; the header one is new (`Add a card to the top of <title>`,
 *    icon-only visible label) and opens a composer at the TOP.
 * 3. **The composer's neighbour pair is computed HERE** (§12.1 — the fifth
 *    `isOptimisticCardId` gate): the bottom composer always gets
 *    `{ previousCardId: null, nextCardId: null }` (§3.2's both-null "bottom
 *    of the list" reading — no neighbour id is needed at all, so the
 *    staleness §12.1 warns about cannot arise); the top composer's
 *    `nextCardId` is the first NON-optimistic card, so a second card typed
 *    in a row never names the placeholder the first one just inserted.
 * 4. **`scroll-py-3` on the card `<ol>`** (§12.2 AC4) — the vertical
 *    analogue of the `scroll-px-3` C2 put on the board strip. The TOP
 *    composer sits inside this scrolling container; the BOTTOM one sits
 *    outside it, in the slot the "Add a card" button already occupied, and
 *    needs nothing.
 * 5. **`<SortableContext>` wraps the `<ol>`'s children**, `items={cardIds}`,
 *    `verticalListSortingStrategy` — the `DndContext` stays in `BoardFace`
 *    (§7.6); this file mounts none of its own.
 * 6. **Quick edit wiring** (§12.4): `quickEditCardId`/`onQuickEditChange`
 *    become `SortableCardTile`'s `quickEditing`/`onQuickEdit`/
 *    `onQuickEditClose` — the third of those is `SortableCardTile`'s own
 *    addition beyond §6.4's fixed four props (its own file header explains
 *    why), and without it `SortableCardTile` deliberately falls back to the
 *    ordinary tile and quick edit never opens at all.
 *
 * ⚠ **A gate beyond §6.5's text, added by this agent and reported in the
 * return**: neither add-card control (nor the composer they open) renders
 * for an OPTIMISTIC list (`isOptimisticListId`), even when `canEdit` is
 * true. `POST /cards` would carry a `listId` the server has never seen —
 * the exact `optimistic:`-prefixed-id-reaches-a-route shape brief §5.4
 * warns about, one level up (the list itself, not a neighbour). `ListHeader`
 * already makes the identical exclusion for rename/menu; this extends the
 * same reasoning to card creation.
 */
import { useId, useState } from 'react';
import { ChevronsRight, Plus } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ApiError,
  BoardFaceLinkCardSurface,
  BoardFaceList,
  CardAgingMode,
  CardMirror,
  PlacedSticker,
  StickerLibraryRow,
  StickerUpdateBody,
} from '@molt/shared';
import { Button, Dialog, IconButton, ListPanel } from '@/components';
import { CardComposer } from '@/components/board/CardComposer';
import type { CardComposerProps } from '@/components/board/CardComposer';
import type { CardSelectionInput } from '@/components/board/CardTile';
import { CardTemplateCreateDialog } from '@/components/board/CardTemplateCreateDialog';
import { ListEmpty } from '@/components/board/BoardStates';
import { ListHeader } from '@/components/board/ListHeader';
import {
  SortableCardTile,
  type CardFrontStickerPlacement,
} from '@/components/board/SortableCardTile';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import type { UndoSourceRegistration } from '@/lib/undo';
import {
  useBoardCardTemplates,
  useCreateCardFromTemplate,
  useHideCardTemplateFromList,
  useUnhideCardTemplateFromList,
} from '@/lib/card-templates';
import { cardDisplayTitle } from '@/lib/card-title';
import { isOptimisticCardId } from '@/lib/cards';
import { announce } from '@/lib/announce';
import { SETTLED } from '@/lib/density';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { listColourStyle } from '@/lib/list-colours';
import { queryKeys } from '@/lib/query-keys';
import { moveSticker, placeSticker, removeSticker, stickerLabel } from '@/lib/stickers';
import { isOptimisticListId } from '@/lib/lists';
import type { DragHandle } from '@/lib/list-drag';
import type { PowerUpSurfaceSummary } from '@/lib/power-ups';

export interface ListColumnProps {
  list: BoardFaceList;
  cardAgingMode?: CardAgingMode | undefined;
  /**
   * `FE-25.2` / `FR-CARD-025` AC5 — the board's `covers_enabled` setting,
   * real on the wire as `boardContentsBoardSchema.coversEnabled`. Threaded
   * exactly as `cardAgingMode` is: a board-level RENDER setting this file
   * neither reads nor defaults, forwarded to `SortableCardTile` and decided
   * one file further down, in `CardTile`.
   *
   * ⚠ **No `?? true` here.** The default lives in exactly ONE place —
   * `CardTile`, the renderer — because defaulting on both sides of one prop
   * is drift nothing can see. See `SortableCardTileProps.coversEnabled`.
   *
   * ⚠ **Optional, and `| undefined` is spelled out rather than left bare.**
   * `tsconfig` sets `exactOptionalPropertyTypes: true`, so a composer holding
   * a `boolean | undefined` could not otherwise pass it through — and the 45
   * render sites in this file's own suite pass no board at all.
   */
  coversEnabled?: boolean | undefined;
  /**
   * `FR-CARD-005`'s optional board setting (`docs/notes/card-keys.md`) —
   * `boardContentsBoardSchema.showCardNumbersOnFront`, threaded on
   * `coversEnabled`'s footing. When `true`, `CardTile` draws the card's `ref`
   * (`MG-0001`) muted before the title; a card whose `ref` is `null` (a viewer
   * outside the reference scope) draws nothing.
   */
  showCardRef?: boolean | undefined;
  /** Owner-directed: board's `dangerousComments` threshold; cards at or above it get a red tint. `null`/undefined = off. */
  dangerousComments?: number | null | undefined;
  canEdit: boolean;
  canWatch?: boolean | undefined;
  /** Opens a composer in the gap to this list's right — `FR-BOARD-009` AC3's keyboard path. Forwarded to `ListHeader`. */
  onInsertListAfter?: ((listId: string) => void) | undefined;
  /** Forwarded to `ListHeader` — `C2-BRIEF.md` §6.4. `ListColumn` does not read it itself. */
  dragHandle?: DragHandle | undefined;
  /*
   * The board owns which one composer is open ANYWHERE on it and which one
   * card is in quick edit — the same single-open discipline C2 established
   * for list composers, extended to cards. Opening either closes the other,
   * and a drag closes both (`BoardFace`, agent G).
   */
  /** Which composer in THIS column is open, if any. */
  openComposer?: 'bottom' | 'top' | null | undefined;
  onOpenComposerChange?: ((placement: 'bottom' | 'top' | null) => void) | undefined;
  /** The card quick edit is open on, if it is in this column. */
  quickEditCardId?: string | null | undefined;
  onQuickEditChange?: ((cardId: string | null) => void) | undefined;
  showLabelNames?: boolean | undefined;
  onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
  onOpenCardDates?: ((intent: BoardCardOpenIntent) => void) | undefined;
  onArchiveCard?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onToggleCardCompleted?: ((card: BoardFaceList['cards'][number]) => void) | undefined;
  onMoveCard?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onCopyCard?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onMoveList?: (() => void) | undefined;
  onCopyList?: (() => void) | undefined;
  onOpenListOperations?: ((list: BoardFaceList) => void) | undefined;
  recordUndoSource?:
    ((source: UndoSourceRegistration) => Promise<void> | void) | undefined;
  onPasteMany?: CardComposerProps['onPasteMany'];
  collapsed?: boolean | undefined;
  onCollapseChange?: ((collapsed: boolean) => void) | undefined;
  highlightedCardId?: string | null | undefined;
  filterActive?: boolean | undefined;
  selectedCardIds?: ReadonlySet<string> | undefined;
  selectionActive?: boolean | undefined;
  onSelectCardGesture?:
    | ((input: CardSelectionInput, card: BoardFaceList['cards'][number]) => void)
    | undefined;
  onOpenBulkCardActions?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onRemoveMirror?: ((mirror: CardMirror) => void) | undefined;
  onConvertLinkCard?: ((card: BoardFaceLinkCardSurface) => void) | undefined;
  pendingSyncCardIds?: ReadonlySet<string> | undefined;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
}

export function ListColumn({
  list,
  cardAgingMode,
  coversEnabled,
  showCardRef,
  dangerousComments,
  canEdit,
  canWatch,
  onInsertListAfter,
  dragHandle,
  openComposer,
  onOpenComposerChange,
  quickEditCardId,
  onQuickEditChange,
  showLabelNames,
  onOpenCard,
  onOpenCardDates,
  onArchiveCard,
  onToggleCardCompleted,
  onMoveCard,
  onCopyCard,
  onMoveList,
  onCopyList,
  onOpenListOperations,
  recordUndoSource,
  onPasteMany,
  collapsed = false,
  onCollapseChange,
  highlightedCardId,
  filterActive = false,
  selectedCardIds,
  selectionActive = false,
  onSelectCardGesture,
  onOpenBulkCardActions,
  onRemoveMirror,
  onConvertLinkCard,
  pendingSyncCardIds,
  powerUpSurfaces,
}: ListColumnProps) {
  const { t } = useInterfaceLanguage();
  const headingId = useId();
  const [templateCreateTarget, setTemplateCreateTarget] = useState<{
    readonly listId: string;
    readonly listTitle: string;
    readonly previousCardId: string | null;
    readonly nextCardId: string | null;
  } | null>(null);
  const [templateCreateResult, setTemplateCreateResult] = useState<{
    readonly path: string;
  } | null>(null);
  const mobileHeadingId = `${headingId}-mobile`;
  const countDescriptionId = `${headingId}-count`;
  const mobileCountDescriptionId = `${headingId}-mobile-count`;
  const collapsedCountDescriptionId = `${headingId}-collapsed-count`;
  const hasCards = list.cards.length > 0;
  // `C2-BRIEF.md` §5.2's table: the placeholder a create inserts before the
  // server has answered carries `aria-busy="true"` on this `<section>` — the
  // element `aria-labelledby` names, and the one thing every consumer of this
  // column can query regardless of what `ListHeader` does internally.
  const optimisticList = isOptimisticListId(list.id);

  // See this file's header: a card cannot be created into a list the server
  // has not confirmed yet, so every add-card affordance below is gated on
  // this rather than on `canEdit` alone.
  const canAddCards = canEdit && !optimisticList;
  const canCollapse = !optimisticList && onCollapseChange !== undefined;
  const composerIsOpen = openComposer !== null && openComposer !== undefined;
  const templateCards = useBoardCardTemplates(
    list.boardId,
    canAddCards && (hasCards || composerIsOpen || templateCreateTarget !== null),
  );
  const createFromTemplate = useCreateCardFromTemplate(list.boardId);
  const hideTemplateCard = useHideCardTemplateFromList(list.boardId);
  const unhideTemplateCard = useUnhideCardTemplateFromList(list.boardId);

  /**
   * ── `FR-CARD-032` AC1 / `FR-EXP-027` AC1–AC2 — THE CARD-FRONT COMPOSER ───
   *
   * `CardTile` ships the drop target and the hover remove control;
   * `SortableCardTile` gates and forwards both. Neither may POST: a `<li>`
   * owns no cache. Until this block existed, **nothing supplied
   * `onPlaceSticker` or `onRequestRemoveSticker` anywhere in the composed
   * application**, so a sticker dragged from the board menu onto a card front
   * was refused and the remove control never rendered — both leaf suites
   * green, both endpoints compiling, the board doing nothing. That is
   * `FE-25.2`'s defect in a second costume: a prop chain whose middle nobody
   * was told they owned.
   *
   * ⚠ **THE COMPOSER IS THIS FILE AND NOT `BoardFace`.** `FR-EXP-027`'s own
   * retired `test.fixme` named `BoardFace.tsx` and `ListColumn.tsx` together,
   * and `BoardFace` is the wrong half: it holds no card, so it would bind
   * nothing this file does not already bind, and it would need TWO more
   * pass-through props through `SortableListColumn` to reach a card. This
   * file already holds `list.boardId`, `canEdit`, every card, and three other
   * mutations of exactly this shape.
   *
   * ⚠ **A LOCAL `useMutation` over the plain exports, never `usePlaceSticker`
   * / `useRemoveSticker`.** Both shipped hooks bind `cardId` at hook
   * construction, and this column holds many cards; calling one per card in
   * the `.map` below would be a hook in a loop. `BoardStickerTray` reached
   * the same conclusion for the same reason.
   *
   * ⚠ **BOTH caches are invalidated, on both mutations.** Dropping
   * `queryKeys.cards.detail` would leave an open card back showing a sticker
   * list the board face has already changed — one surface right and one wrong,
   * the drift `placedStickerSchema` is shared to prevent.
   */
  const queryClient = useQueryClient();
  const [removeTarget, setRemoveTarget] = useState<{
    readonly cardId: string;
    readonly sticker: PlacedSticker;
  } | null>(null);

  /**
   * The drop payload is a sticker ID and nothing else (`STICKER_DRAG_TYPE`
   * carries one string), so the NAME the announcement needs is read back from
   * the library already in cache — AC1's premise is that the tray is open, so
   * `GET /boards/:id/stickers` has resolved. `getQueryData`, never a second
   * `useQuery`: a miss is an empty announcement slot, not a request.
   */
  function libraryStickerName(stickerId: string): string {
    const library =
      queryClient.getQueryData<StickerLibraryRow[]>(
        queryKeys.boards.stickers(list.boardId),
      ) ?? [];
    const row = library.find((entry) => entry.id === stickerId);
    return row === undefined ? '' : stickerLabel(t, row);
  }

  const placeCardSticker = useMutation<
    PlacedSticker,
    ApiError,
    { readonly cardId: string; readonly placement: CardFrontStickerPlacement }
  >({
    mutationFn: (variables) =>
      placeSticker(variables.cardId, {
        stickerId: variables.placement.stickerId,
        surface: 'front',
        positionX: variables.placement.positionX,
        positionY: variables.placement.positionY,
        rotation: 0,
        zIndex: 0,
      }),
    onSuccess: (_placed, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(list.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
      announce(
        t('cardBack.stickers.announce.added', {
          sticker: libraryStickerName(variables.placement.stickerId),
        }),
      );
    },
    onError: () => announce(t('cardBack.stickers.announce.placeFailed')),
  });

  const removeCardSticker = useMutation<
    null,
    ApiError,
    { readonly cardId: string; readonly sticker: PlacedSticker }
  >({
    mutationFn: (variables) => removeSticker(variables.sticker.id),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(list.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
      announce(
        t('cardBack.stickers.announce.removed', {
          sticker: stickerLabel(t, variables.sticker.sticker),
        }),
      );
      setRemoveTarget(null);
    },
    onError: () => announce(t('cardBack.stickers.announce.removeFailed')),
  });

  const moveCardSticker = useMutation<
    PlacedSticker,
    ApiError,
    {
      readonly cardId: string;
      readonly sticker: PlacedSticker;
      readonly body: StickerUpdateBody;
    }
  >({
    mutationFn: (variables) => moveSticker(variables.sticker.id, variables.body),
    onSuccess: (_sticker, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(list.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
    },
    onError: () => announce(t('cardBack.stickers.announce.moveFailed')),
  });

  function confirmRemoveSticker() {
    if (removeTarget === null) return;
    removeCardSticker.mutate(removeTarget);
  }
  const availableTemplateCards = templateCards.data ?? [];
  const templateCardsLoading =
    templateCards.isPending === true || templateCards.isFetching === true;
  const templateCardById = new Map(
    availableTemplateCards.map((templateCard) => [templateCard.id, templateCard]),
  );
  const visibleCards = list.cards.filter(
    (card) => templateCardById.get(card.id)?.hiddenFromList !== true,
  );
  const hiddenVisibleTemplateCount = list.cards.length - visibleCards.length;

  const cardIds = visibleCards.map((card) => card.id);
  const visibleOpenCardCount = Math.max(
    0,
    list.openCardCount - hiddenVisibleTemplateCount,
  );
  const cardCountDescription = t(
    'boardFace.list.cardCount',
    { count: visibleOpenCardCount },
    visibleOpenCardCount,
  );

  // §12.1's top row: the first NON-optimistic card, walking past any
  // placeholder — mirrors `neighboursForMove`'s own `excludeIds` reasoning.
  const firstRealCardId =
    visibleCards.find((card) => !isOptimisticCardId(card.id))?.id ?? null;

  function openBottomComposer() {
    onOpenComposerChange?.('bottom');
  }

  function openTopComposer() {
    onOpenComposerChange?.('top');
  }

  function handleBottomOpenChange(open: boolean) {
    onOpenComposerChange?.(open ? 'bottom' : null);
  }

  function handleTopOpenChange(open: boolean) {
    onOpenComposerChange?.(open ? 'top' : null);
  }

  function openTemplateCreator(input: {
    readonly listId: string;
    readonly listTitle: string;
    readonly previousCardId: string | null;
    readonly nextCardId: string | null;
  }) {
    setTemplateCreateResult(null);
    setTemplateCreateTarget(input);
  }

  function closeTemplateCreator() {
    setTemplateCreateTarget(null);
    setTemplateCreateResult(null);
  }

  function createTemplateCard(input: {
    readonly templateCardId: string;
    readonly title: string;
  }) {
    if (templateCreateTarget === null) return;
    const templateCard = availableTemplateCards.find(
      (card) => card.id === input.templateCardId,
    );
    createFromTemplate.mutate(
      {
        boardId: list.boardId,
        templateCardId: input.templateCardId,
        /*
         * FE-80.1 display-name fallbacks — THE LEAF, not `?? 'template card'`.
         *
         * `templateTitle` is client-only, and that is the load-bearing fact —
         * routing DATA would be a fake fix. `useCreateCardFromTemplate`'s
         * `mutationFn` (`lib/card-templates.ts:220`) names the four fields it
         * sends (`listId`, `previousCardId`, `nextCardId`, `title`) and does
         * not forward the variables object, so this value never reaches the
         * wire. Its one reader is the `onError` announcement at `:251`, which
         * puts it into `cardTemplateHooks.createFailed`'s `{title}` slot — so
         * the English literal that used to sit here stood inside a routed
         * sentence, and nowhere else.
         *
         * ⚠ `?? ''` and not `?? 'Untitled card'`, the move `ArchivedItemsPanel.
         * tsx:492` records: the original was a NULLISH fallback and
         * `cardDisplayTitle`'s is a trim-empty one, so the empty string routes
         * through the same predicate to `board.card.untitled` — the key that
         * already owns a card's fallback title, and a template card is a card.
         * No new key, no tenth author.
         *
         * ⚠ A guard-and-return would be wrong HERE even though the sibling in
         * `BoardFace` takes one. That proof is same-scope; this one crosses a
         * component boundary (`CardTemplateCreateDialog` validates its
         * selection against the very `templates` array this closure was handed
         * in the same render, and `submitCreate` refuses an empty selection),
         * so if it were wrong a guard would make Create silently do nothing.
         * Preserving the mutation and changing only the noun cannot.
         */
        templateTitle: cardDisplayTitle(t, templateCard?.title ?? ''),
        destinationListId: templateCreateTarget.listId,
        previousCardId: templateCreateTarget.previousCardId,
        nextCardId: templateCreateTarget.nextCardId,
        title: input.title,
      },
      {
        onSuccess: (result) => {
          // FE-80.1 card composer. This announcement is the spoken OUTCOME of
          // `CardComposer`'s `Create from template` button — the same gesture,
          // one file up the callback chain — so it is routed by that slice
          // rather than left for whichever later one takes the rest of this
          // file. Leaving it would have made the button Spanish and its result
          // English, which is the half-translated shape the plan's residual 4
          // records as reading like a bug to anyone who meets it cold.
          announce(t('boardFace.card.createdFromTemplate', { title: result.card.title }));
          setTemplateCreateResult({ path: result.card.path });
        },
      },
    );
  }

  const expandedColumn = (
    <section
      aria-labelledby={collapsed ? mobileHeadingId : headingId}
      aria-describedby={collapsed ? mobileCountDescriptionId : countDescriptionId}
      aria-busy={optimisticList ? true : undefined}
      data-board-list-id={list.id}
      className={
        collapsed
          ? 'flex min-h-0 w-full flex-col max-h-full md:hidden'
          : 'flex min-h-0 w-full flex-col max-h-full'
      }
    >
      <span
        id={collapsed ? mobileCountDescriptionId : countDescriptionId}
        className="sr-only"
      >
        {cardCountDescription}
      </span>
      <ListPanel
        className="flex min-h-0 flex-1 flex-col overflow-hidden border border-transparent"
        style={listColourStyle(list.colour)}
        data-list-colour={list.colour === 'none' ? undefined : list.colour}
      >
        <div className="flex items-start gap-1">
          <div className="min-w-0 flex-1">
            {/*
              ⚠ Passed as plain props, NOT as conditional JSX spreads, and the
              parent changed both this and `ListHeaderProps` to `| undefined` at
              integration to make it possible. **JSX spread attributes bypass
              excess-property checking**, so the spread form compiled while
              `ListHeaderProps` still declared neither prop — the drag-handle
              plumbing, without which `FR-BOARD-011` does not exist at all, was
              verified by nothing and `tsc` said so with a green tick.
              `exactOptionalPropertyTypes` is what pushed both agents into the
              spread; `| undefined` on the declaration is the honest way out.
            */}
            <ListHeader
              list={list}
              canEdit={canEdit}
              canWatch={canWatch}
              headingId={collapsed ? mobileHeadingId : headingId}
              onInsertListAfter={onInsertListAfter}
              onMoveList={onMoveList}
              onCopyList={onCopyList}
              onOpenListOperations={
                onOpenListOperations ? () => onOpenListOperations(list) : undefined
              }
              recordUndoSource={recordUndoSource}
              collapsed={false}
              onCollapseChange={canCollapse ? onCollapseChange : undefined}
              dragHandle={dragHandle}
              powerUpSurfaces={powerUpSurfaces}
            />
          </div>

          {canAddCards && (
            /*
             * `FE-12.1`, `FR-BOARD-020` AC2 — the header add control.
             * §7.1's fixed accessible name and icon-only visible label.
             * `ListHeader.tsx` is not this chunk's file to edit, so this
             * sits as a SIBLING in a wrapping flex row rather than inside
             * that component's own markup.
             */
            <IconButton
              label={t('boardFace.list.addCardToTop', { list: list.title })}
              size="sm"
              /*
               * ⚠ `mt-1` was here and the screenshot pass removed it — §12.18
               * asked whether it was "an avoidable 4px if the row grew", and
               * the browser answer is that it costs **0px of height and 4px of
               * misalignment**, which is worse than the question assumed.
               *
               * Measured at 1440×900: this flex row is **36px** tall, and it
               * is 36 *because of `ListHeader` alone* — that component's root
               * is `items-center … pb-2`, so its 28px `IconButton`s occupy
               * rows 0–28 and its `pb-2` supplies the last 8. This button is
               * `size-7` (28px) in an `items-start` row, so `mt-1` put it at
               * rows 4–32: four pixels below the grip, the card count and the
               * list menu it sits in line with, and visibly so at 1:1. Without
               * it the button is at 0–28, exactly level with them, and the row
               * is still 36 — `ListHeader` sets that number either way.
               *
               * ⚠ **And it stays 36 in every state that renders this button**,
               * not merely in the one that was measured: `canAddCards` here is
               * `canEdit && !optimisticList`, and `SortableListColumn`'s
               * `canAct` — the sole gate on the `dragHandle` that renders
               * `ListHeader`'s 28px grip — is `canEdit && !isPlaceholder`.
               * The two conditions are the same condition, so this button
               * cannot appear in a header row that has no 28px `IconButton`
               * of its own to set the height. Were they ever decoupled, a
               * grip-less row would collapse toward the 20px heading plus
               * `pb-2` and this button would become the tallest thing in it —
               * which is the state in which `mt-1` would have cost a real 4px.
               *
               * So this changes no measured density at all (`density.ts` is
               * untouched, and `SETTLED.panelHeaderHeight` is unaffected by
               * this button in either state — see the screenshot pass's return
               * for the separate finding that the constant's own value of 28
               * is 8px short of the measured 36).
               */
              className="shrink-0"
              onClick={openTopComposer}
            >
              <Plus aria-hidden="true" className="size-3.5" />
            </IconButton>
          )}
        </div>

        {/*
          `FR-VIEW-007` AC3 / `C3-BRIEF.md` §6.5, §12.13 item 4: this `<ol>`
          ALWAYS renders, even when the list has no cards — it is the drop
          target that makes `FR-BOARD-025`'s "drag into an empty list"
          possible. `ListEmpty` renders INSIDE it, never instead of it.
        */}
        <ol
          aria-label={t('boardFace.list.cardStack', { list: list.title })}
          data-board-card-stack-list-id={list.id}
          // `scroll-py-3` — §12.2 AC4's vertical analogue of the
          // `scroll-px-3` C2 put on the board strip. The TOP composer lives
          // inside this scrolling container; without scroll padding a list
          // scrolled away from the top can clip it exactly the way C2's own
          // list composer was clipped horizontally.
          className="flex min-h-0 flex-1 flex-col overflow-y-auto scroll-py-3"
          style={{ gap: `${SETTLED.cardGap}px` }}
        >
          <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
            {openComposer === 'top' && canAddCards && (
              <li>
                <CardComposer
                  boardId={list.boardId}
                  listId={list.id}
                  listTitle={list.title}
                  placement="top"
                  previousCardId={null}
                  nextCardId={firstRealCardId}
                  open
                  onOpenChange={handleTopOpenChange}
                  onPasteMany={onPasteMany}
                  canCreateFromTemplate={availableTemplateCards.length > 0}
                  templateLoading={templateCardsLoading}
                  onCreateFromTemplate={openTemplateCreator}
                />
              </li>
            )}

            {visibleCards.length > 0 ? (
              visibleCards.map((card, index) => {
                const templateState = templateCardById.get(card.id);
                return (
                  <SortableCardTile
                    key={card.id}
                    card={card}
                    cardAgingMode={cardAgingMode}
                    /*
                     * `FE-25.2` — a PLAIN prop, never
                     * `{...(coversEnabled === undefined ? {} : { coversEnabled })}`.
                     * A conditional JSX spread bypasses excess-property
                     * checking, so it compiles against props that were never
                     * declared — the recorded C2 defect, and the reason
                     * `SortableCardTileProps.coversEnabled` names that spread
                     * in its own docstring as the shape not to write.
                     */
                    coversEnabled={coversEnabled}
                    showCardRef={showCardRef}
                    dangerousComments={dangerousComments}
                    listId={list.id}
                    listTitle={list.title}
                    cardIndex={index}
                    listCardCount={visibleCards.length}
                    canEdit={canEdit}
                    quickEditing={card.id === quickEditCardId}
                    onQuickEdit={
                      onQuickEditChange ? () => onQuickEditChange(card.id) : undefined
                    }
                    onQuickEditClose={
                      onQuickEditChange ? () => onQuickEditChange(null) : undefined
                    }
                    onArchive={
                      onArchiveCard ? () => onArchiveCard(card, list.id) : undefined
                    }
                    onToggleCompleted={
                      onToggleCardCompleted
                        ? () => onToggleCardCompleted(card)
                        : undefined
                    }
                    onMove={onMoveCard ? () => onMoveCard(card, list.id) : undefined}
                    onCopy={onCopyCard ? () => onCopyCard(card, list.id) : undefined}
                    /*
                     * `FR-CARD-032` AC1 / `FR-EXP-027` AC1 and AC2 — the two
                     * card-front sticker callbacks, bound to THIS card exactly
                     * as `onArchive` and `onCopy` above are bound. Plain props,
                     * never a conditional spread.
                     *
                     * `canEdit` and not `canAddCards`: an optimistic LIST id
                     * does not make its cards unplaceable — the card ids are
                     * real. `SortableCardTile` re-gates both on `canAct`,
                     * which is where a placeholder CARD is refused.
                     */
                    onPlaceSticker={
                      canEdit
                        ? (placement) =>
                            placeCardSticker.mutate({ cardId: card.id, placement })
                        : undefined
                    }
                    onRequestRemoveSticker={
                      canEdit
                        ? (sticker) => setRemoveTarget({ cardId: card.id, sticker })
                        : undefined
                    }
                    onAdjustSticker={
                      canEdit
                        ? (sticker, body) =>
                            moveCardSticker.mutate({ cardId: card.id, sticker, body })
                        : undefined
                    }
                    showLabelNames={showLabelNames}
                    onOpenCard={onOpenCard}
                    onOpenDates={onOpenCardDates}
                    highlighted={card.id === highlightedCardId}
                    selected={selectedCardIds?.has(card.id) ?? false}
                    selectionActive={selectionActive}
                    onSelectGesture={onSelectCardGesture}
                    onOpenBulkActions={
                      onOpenBulkCardActions
                        ? () => onOpenBulkCardActions(card, list.id)
                        : undefined
                    }
                    onRemoveMirror={onRemoveMirror}
                    onConvertLinkCard={onConvertLinkCard}
                    pendingSync={pendingSyncCardIds?.has(card.id) ?? false}
                    powerUpSurfaces={powerUpSurfaces}
                    templateState={
                      templateState === undefined
                        ? undefined
                        : {
                            isTemplate: templateState.isTemplate,
                            hiddenFromList: templateState.hiddenFromList,
                            busy:
                              (hideTemplateCard.isPending === true &&
                                hideTemplateCard.variables?.cardId === card.id) ||
                              (unhideTemplateCard.isPending === true &&
                                unhideTemplateCard.variables?.cardId === card.id),
                          }
                    }
                    onHideTemplateCard={
                      canEdit
                        ? () =>
                            hideTemplateCard.mutate({
                              cardId: card.id,
                              title: card.title,
                            })
                        : undefined
                    }
                    onUnhideTemplateCard={
                      canEdit
                        ? () =>
                            unhideTemplateCard.mutate({
                              cardId: card.id,
                              title: card.title,
                            })
                        : undefined
                    }
                  />
                );
              })
            ) : filterActive ? (
              <li>
                <div className="rounded-card border border-dashed border-border-strong bg-surface-raised px-3 py-2 text-sm text-ink">
                  {t('boardFace.list.noFilterMatches')}
                </div>
              </li>
            ) : (
              <li>
                {/*
                  ⚠ `canEdit={canAddCards}`, NOT the raw `canEdit` prop this
                  file received — a deliberate narrowing of what `ListEmpty`'s
                  own `canEdit` means FOR THIS CALLER. `ListEmpty`'s contract
                  (`BoardStates.tsx`) is "show the action when the viewer can
                  edit"; this call site asks the narrower question "…and this
                  specific list can accept a card right now", which is false
                  for a viewer who can edit but whose list is still
                  optimistic. `BoardStates.test.tsx` renders `ListEmpty`
                  directly and cannot see this narrowing — it is local to this
                  call site, not a change to `ListEmptyProps` itself.
                */}
                <ListEmpty
                  listTitle={list.title}
                  canEdit={canAddCards}
                  onAddCard={canAddCards ? openBottomComposer : undefined}
                />
              </li>
            )}
          </SortableContext>
        </ol>

        {canAddCards &&
          (openComposer === 'bottom' ? (
            /*
             * §3.2 / §12.1: both-null IS the bottom of the list, on both
             * `POST /cards` and `POST /cards/:id/move` — no neighbour id is
             * needed, so the staleness §12.1 warns about (a neighbour that
             * becomes an `optimistic:` placeholder between two successive
             * submits) cannot arise for this composer at all.
             */
            <CardComposer
              boardId={list.boardId}
              listId={list.id}
              listTitle={list.title}
              placement="bottom"
              previousCardId={null}
              nextCardId={null}
              open
              onOpenChange={handleBottomOpenChange}
              onPasteMany={onPasteMany}
              canCreateFromTemplate={availableTemplateCards.length > 0}
              templateLoading={templateCardsLoading}
              onCreateFromTemplate={openTemplateCreator}
              className="mt-2 shrink-0"
            />
          ) : (
            visibleCards.length > 0 && (
              /*
               * ⚠ **The list title is in the accessible name and NOT in the
               * visible label**, and the difference is the whole point.
               * `C1-BRIEF.md` §7.3 fixed the accessible name as `Add a card
               * to <list title>` so that a screen reader user hears which of
               * five identical buttons they are on — and the first build
               * rendered that string *visibly*, which put a 40-character
               * board-seeded list name inside a 292px column and wrapped it
               * to two lines. Nothing failed: every test queries by
               * accessible name, so the suite was green and the board was
               * unreadable. It was caught by looking at a screenshot.
               *
               * WCAG 2.5.3 (Label in Name) is satisfied because the
               * accessible name *begins with* the visible label — a
               * voice-control user saying "add a card" still matches.
               */
              <Button
                variant="quiet"
                size="sm"
                className="mt-2 shrink-0 justify-start"
                aria-label={t('boardFace.list.addCardTo', { list: list.title })}
                onClick={openBottomComposer}
              >
                {t('boardFace.list.addCard')}
              </Button>
            )
          ))}
      </ListPanel>
    </section>
  );

  if (collapsed) {
    return (
      <>
        {expandedColumn}
        <section
          aria-labelledby={headingId}
          aria-describedby={collapsedCountDescriptionId}
          data-board-list-id={list.id}
          className="hidden min-h-0 w-full max-h-full md:flex"
        >
          <span id={collapsedCountDescriptionId} className="sr-only">
            {cardCountDescription}
          </span>
          <ListPanel
            className="flex min-h-0 flex-1 flex-col items-center overflow-hidden px-1 py-2"
            style={listColourStyle(list.colour)}
            data-list-colour={list.colour === 'none' ? undefined : list.colour}
          >
            <IconButton
              label={t('boardFace.list.expand', { list: list.title })}
              size="sm"
              onClick={() => onCollapseChange?.(false)}
            >
              <ChevronsRight aria-hidden="true" className="size-3.5" />
            </IconButton>
            <h2
              id={headingId}
              title={list.title}
              className="mt-2 min-h-0 flex-1 overflow-hidden font-display text-sm font-semibold"
            >
              <span className="block [writing-mode:vertical-rl]">{list.title}</span>
            </h2>
            <span
              aria-label={cardCountDescription}
              className="mt-2 shrink-0 rounded-full bg-surface-raised px-2 py-0.5 text-xs font-semibold text-ink-muted"
            >
              <span aria-hidden="true">{visibleOpenCardCount}</span>
              <span className="sr-only">{cardCountDescription}</span>
            </span>
          </ListPanel>
        </section>
      </>
    );
  }

  return (
    <>
      {expandedColumn}
      {templateCreateTarget !== null ? (
        <CardTemplateCreateDialog
          open
          templates={availableTemplateCards}
          destinationListTitle={templateCreateTarget.listTitle}
          loading={templateCards.isPending}
          busy={createFromTemplate.isPending}
          createdCardPath={templateCreateResult?.path}
          onCancel={closeTemplateCreator}
          onCreate={createTemplateCard}
        />
      ) : null}
      {/*
        `FR-EXP-027` AC2 — *"hovers it and selects remove **and confirms**"*.
        `FR-CARD-032`'s behaviour text: sticker deletion has no undo, matching
        Trello, so it requires a confirmation. THE SAME DIALOG AS THE CARD
        BACK'S, under the same four `cardBack.stickers.remove.*` keys — one
        confirmation, two entry points, and no new catalogue key (rule 9's
        cheapest outcome).

        ⚠ The `title` ternary is load-bearing, not tidy — copied from
        `CardStickerSection` with its reasoning: JSX props evaluate eagerly, so
        this `t()` fires on every render of the column whether the dialog is
        open or closed, and `interfaceMessage` THROWS on a slot whose value is
        `undefined`. The empty string is a value and does not throw.
      */}
      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title={t('cardBack.stickers.remove.title', {
          sticker:
            removeTarget === null ? '' : stickerLabel(t, removeTarget.sticker.sticker),
        })}
        description={t('cardBack.stickers.remove.description')}
        footer={
          <>
            <Button variant="quiet" onClick={() => setRemoveTarget(null)}>
              {t('cardBack.stickers.remove.cancel')}
            </Button>
            <Button
              variant="danger"
              busy={removeCardSticker.isPending}
              onClick={confirmRemoveSticker}
            >
              {t('cardBack.stickers.remove.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">{t('cardBack.stickers.remove.body')}</p>
      </Dialog>
    </>
  );
}
